/**
 * Assistente por etapas: indicador de progresso, uma etapa por vez e navegação Voltar /
 * Continuar. Os dados ficam com a tela que usa o assistente (nada se perde ao voltar); o
 * assistente só desenha. Etapas ainda não alcançadas não podem ser abertas pelo indicador.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "./icons";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

export type WizardStep = { key: string; label: string };

export function StepIndicator({
  steps,
  current,
  reached,
  onStep,
}: {
  steps: WizardStep[];
  /** índice da etapa atual (0…) */
  current: number;
  /** maior índice já alcançado (as etapas até ele podem ser reabertas) */
  reached: number;
  onStep?: (index: number) => void;
}) {
  return (
    <ol
      aria-label="Etapas"
      className="flex flex-wrap items-center gap-x-1.5 gap-y-2 rounded-xl border bg-card px-3 py-2.5 shadow-card"
    >
      {steps.map((s, i) => {
        const isCurrent = i === current;
        const done = i < current || (i <= reached && !isCurrent);
        const locked = i > reached;
        return (
          <li key={s.key} className="flex items-center gap-1.5">
            <button
              type="button"
              aria-current={isCurrent ? "step" : undefined}
              disabled={locked || !onStep}
              onClick={() => onStep?.(i)}
              className={cx(
                "flex min-h-10 items-center gap-2 rounded-lg px-2.5 text-sm",
                isCurrent ? "bg-muted font-semibold" : "font-medium",
                locked ? "cursor-default text-muted-foreground" : "hover:bg-muted",
              )}
            >
              <span
                aria-hidden
                className={cx(
                  "flex size-7 items-center justify-center rounded-full text-[13px] font-semibold",
                  isCurrent && "bg-accent text-accent-foreground",
                  !isCurrent && done && "bg-success-soft text-success",
                  !isCurrent && !done && "border border-input text-muted-foreground",
                )}
              >
                {done && !isCurrent ? <Icon name="check" size={14} strokeWidth={3} /> : i + 1}
              </span>
              <span>{s.label}</span>
              {done && !isCurrent && <span className="sr-only">(concluída)</span>}
            </button>
            {i < steps.length - 1 && (
              <span aria-hidden className="h-0.5 w-5 rounded bg-input sm:w-7" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function StepWizard({
  steps,
  current,
  reached,
  onStep,
  onBack,
  onNext,
  nextLabel = "Continuar",
  nextDisabled = false,
  finalActions,
  onCancel,
  cancelLabel = "Cancelar",
  aside,
  children,
}: {
  steps: WizardStep[];
  current: number;
  reached: number;
  onStep: (index: number) => void;
  onBack: () => void;
  /** Continuar: quem usa valida a etapa e só então avança */
  onNext: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** ações da última etapa (ex.: Finalizar) no lugar de Continuar */
  finalActions?: ReactNode;
  /** Cancelar (só na primeira etapa, no lugar de Voltar) */
  onCancel?: () => void;
  cancelLabel?: string;
  /** resumo ao lado (opcional) */
  aside?: ReactNode;
  children: ReactNode;
}) {
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const first = useRef(true);
  useEffect(() => {
    // ao trocar de etapa, o foco vai para o título (leitor de tela anuncia a etapa)
    if (first.current) {
      first.current = false;
      return;
    }
    titleRef.current?.focus();
  }, [current]);
  const last = current === steps.length - 1;
  const step = steps[current];
  return (
    <div className="flex flex-col gap-5">
      <StepIndicator steps={steps} current={current} reached={reached} onStep={onStep} />
      <div className="flex flex-wrap items-start gap-5">
        <section className="flex min-w-0 flex-[999_1_560px] flex-col rounded-xl border bg-card shadow-card">
          <header className="border-b border-border/70 px-5 py-4">
            <p className="text-[13px] font-semibold text-accent">
              Etapa {current + 1} de {steps.length}
            </p>
            <h2 ref={titleRef} tabIndex={-1} className="text-lg font-semibold outline-none">
              {step?.label}
            </h2>
          </header>
          <div className="flex flex-col gap-5 p-5">{children}</div>
          <footer className="mt-auto flex flex-wrap items-center gap-2.5 border-t border-border/70 px-5 py-4">
            {current === 0 && onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="h-11 rounded-lg border border-input bg-card px-4 text-sm font-medium hover:bg-muted"
              >
                {cancelLabel}
              </button>
            )}
            {current > 0 && (
              <button
                type="button"
                onClick={onBack}
                className="h-11 rounded-lg border border-input bg-card px-4 text-sm font-medium hover:bg-muted"
              >
                Voltar
              </button>
            )}
            <span className="flex-1" />
            {last ? (
              finalActions
            ) : (
              <button
                type="button"
                onClick={onNext}
                disabled={nextDisabled}
                className="h-11 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {nextLabel}
              </button>
            )}
          </footer>
        </section>
        {aside && (
          <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-3 rounded-xl border bg-card p-5 shadow-card lg:max-w-sm">
            {aside}
          </aside>
        )}
      </div>
    </div>
  );
}
